/**
 * Root route table. Maps directly to PRD §14's screen inventory plus the
 * Admin-only pages from the locked access model.
 *
 * Route guard pattern: every authenticated route is wrapped in
 * <RequireRole>. /view/:token and /reset-password are the two public,
 * unauthenticated surfaces in the app — deliberately outside any guard.
 * /reset-password specifically must never require an existing session:
 * the entire point of a password reset is recovering access without one.
 */

import { Routes, Route } from 'react-router-dom';
import { RequireRole } from './auth/RequireRole';
import { LoginPage } from './pages/LoginPage';
import { ResetPasswordPage } from './pages/ResetPasswordPage';
import { DashboardPage } from './pages/DashboardPage';
import { EditorPage } from './pages/EditorPage';
import { SetupSheetPreview } from './pages/SetupSheetPreview';
import { TemplateLibraryPage } from './pages/TemplateLibraryPage';
import { AdminInventoryPage } from './pages/AdminInventoryPage';
import { AdminVenueSpacesPage } from './pages/AdminVenueSpacesPage';
import { AdminUsersPage } from './pages/AdminUsersPage';
import { BrideShareView } from './pages/BrideShareView';
import { NotFoundPage } from './pages/NotFoundPage';
import { AppLayout } from './components/shared/AppLayout';

export function App() {
  return (
    <Routes>
      {/* Public, unauthenticated — no RequireRole wrapper. */}
      <Route path="/view/:token" element={<BrideShareView />} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />

      <Route path="/login" element={<LoginPage />} />

      <Route
        path="/"
        element={
          <RequireRole allow={['admin', 'coordinator']}>
            <AppLayout>
              <DashboardPage />
            </AppLayout>
          </RequireRole>
        }
      />

      <Route
        path="/weddings/:weddingId"
        element={
          <RequireRole allow={['admin', 'coordinator']}>
            <AppLayout>
              <EditorPage />
            </AppLayout>
          </RequireRole>
        }
      />

      <Route
        path="/weddings/:weddingId/setup-sheet"
        element={
          <RequireRole allow={['admin', 'coordinator']}>
            <SetupSheetPreview />
          </RequireRole>
        }
      />

      <Route
        path="/templates"
        element={
          <RequireRole allow={['admin', 'coordinator']}>
            <AppLayout>
              <TemplateLibraryPage />
            </AppLayout>
          </RequireRole>
        }
      />

      {/* Admin-only — both RLS (database) and this route guard (UI) enforce
          this boundary, per the locked access model: room dimensions,
          object catalog, inventory limits, and venue-wide settings stay
          Admin-only to prevent accidental changes affecting every future
          layout. */}
      <Route
        path="/admin/inventory"
        element={
          <RequireRole allow={['admin']}>
            <AppLayout>
              <AdminInventoryPage />
            </AppLayout>
          </RequireRole>
        }
      />

      <Route
        path="/admin/venue-spaces"
        element={
          <RequireRole allow={['admin']}>
            <AppLayout>
              <AdminVenueSpacesPage />
            </AppLayout>
          </RequireRole>
        }
      />

      <Route
        path="/admin/users"
        element={
          <RequireRole allow={['admin']}>
            <AppLayout>
              <AdminUsersPage />
            </AppLayout>
          </RequireRole>
        }
      />

      {/* Catch-all — must stay last. Works regardless of login state, so
          a broken/expired link never produces a blank screen. */}
      <Route path="*" element={<NotFoundPage />} />
    </Routes>
  );
}
